/* eslint-disable import/no-cycle */
import React from 'react';
import { Interpolation, Theme } from '@emotion/react';
import { useMicrosite } from '../../../contexts/franchise/microsite-context';
import { PolymorphicWithoutRef } from '../polymorphic';
import type { CollapsibleTextProps } from '../collapsible-text';
import CollapsibleText from '../collapsible-text';
import RichTextEditorStyles from '../../utils/RichTextEditorStyles';

const replacer = (
    permalink: string|null,
    allowedInternalLinks: (string|null)[]|null,
) => (
    match: string,
    href: string,
    anchorInnerText: string,
): string => {
    // replace if href is a relative path and we have a permalink
    if (!!href && !!permalink && href.startsWith('/')) {
        // replace <a> with <span> if not allowed
        if (!allowedInternalLinks?.includes(href)) {
            return `<span>${anchorInnerText}</span>`;
        }
        // path is allowed. add microsite permalink
        return match.replace(/href=['"](.*?)['"]/g, `href="${permalink}${href}"`);
    }
    return match;
};

const micrositeHTMLAnchorNormalizer = (
    copy: string,
    permalink: string|null,
    allowedInternalLinks: (string|null)[]|null,
): string => {
    // regex for anchor tags with an href that is not an empty string
    const anchorRegex = /<a\s+(?:[^>]*?\s+)?href=['"]([^\s"]+)['"][^>]*?>(.*?)<\/a>/gm;
    return copy.replace(anchorRegex, replacer(permalink, allowedInternalLinks));
};

const RichText = <Component extends React.ElementType = 'div'>(
    props: PolymorphicWithoutRef<Component, CollapsibleTextProps>,
): JSX.Element => {
    const {
        children,
        maxLength,
        disabled,
        ...rest
    } = props;

    // eslint-disable-next-line no-underscore-dangle
    let copySource = children;

    const {
        isMicrosite,
        permalink,
        allowedInternalLinks,
    } = useMicrosite();

    if (typeof copySource === 'string') {
        if (isMicrosite) {
            copySource = micrositeHTMLAnchorNormalizer(copySource, permalink, allowedInternalLinks);
        }
    }

    if (!maxLength) {
        const isDangerous = typeof copySource === 'string';

        const Component = props?.as || 'div';

        return (
            <Component
                css={[RichTextEditorStyles as Interpolation<Theme>, {
                    div: { display: 'inline' },
                    p: {
                        marginBottom: '.5rem',
                    },
                }]}
                {...rest}
                dangerouslySetInnerHTML={isDangerous ? { __html: copySource as string } : undefined}
            >
                {!isDangerous ? copySource : undefined}
            </Component>
        );
    }

    return (
        <CollapsibleText
            css={[RichTextEditorStyles as Interpolation<Theme>, {
                div: { display: 'inline' },
                p: {
                    marginBottom: '.5rem',
                },
            }]}
            maxLength={maxLength}
            disabled={disabled}
            {...rest}
        >
            {copySource}
        </CollapsibleText>
    );
};

export default RichText;
